/**
 * The System → Audit log's filter + sort declaration — a consumer of the
 * generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` / `withdrawalsFilterConfig`.
 *
 * Facets exposed on the bar:
 *   - search q   → `q`          debounced free text. Maps to the backend's
 *                               `q` param, a LIKE over the `action` column
 *                               (e.g. `students.` matches every student
 *                               mutation). This REPLACES the old free-text
 *                               "Action" input — a substring search is a strict
 *                               superset of the prior "type an action prefix"
 *                               behaviour.
 *   - targetType → `targetType` multi-select over the entity kinds an audit row
 *                               can point at. The audit table stores this as a
 *                               free-form text column (not a DB enum), so the
 *                               backend accepts a comma list and sanitises each
 *                               token; the option list here is a curated subset
 *                               of the high-traffic types, not the exhaustive
 *                               ~50 internal ones. allEncoding 'omit'.
 *   - actorRole  → `actorRole`  multi-select over the role slugs. Same free-form
 *                               text column + comma-list contract. allEncoding
 *                               'omit'.
 *   - occurred   → `from`/`to`  date range over the row's `occurredAt` timestamp.
 *
 * NOTE: `actor` (an EXACT actor user-id match, `eq` on the backend) stays a
 * SIBLING text input on the route — NOT a toolkit facet — exactly like the
 * Withdrawals queue keeps `academicYear` a sibling single-select. A free-text id
 * has no fixed option set (so it can't be a multi-select) and is matched with a
 * single `eq` (so a comma-joined value would break it). It lives in the same URL
 * search contract (`auditSearchSchema`) and the route folds it into the one
 * query alongside `apiParams`.
 *
 * IMPORTANT (route responsibility): the toolkit's date-range facet emits
 * date-only `YYYY-MM-DD` bounds, but the audit backend compares them RAW against
 * ISO-datetime `occurredAt` values (unlike the announcements backend, which pads
 * date-only bounds to whole-day UTC server-side). A bare `to=2026-06-30` would
 * therefore exclude every event on the 30th. The audit route pads the resolved
 * `from`/`to` to whole-day UTC before sending — see `_authed.system.audit.tsx`.
 * This is deliberately kept out of the shared toolkit: the announcements backend
 * requires exactly `^\d{4}-\d{2}-\d{2}$` and returns "no constraint" on anything
 * else, so a global pad would silently disable its date filter.
 *
 * Sorts map to the backend's single allow-listed `sort` field (`occurredAt`),
 * both directions. The default (first entry) is "Newest first" = occurredAt:desc
 * — the log's pre-existing order.
 *
 * Sortable column header: Occurred (occurredAt). Both directions are registered
 * so the header can toggle desc↔asc without snapping back to the default sort.
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const auditFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by action',
    },
    {
      kind: 'multi-select',
      key: 'targetType',
      label: 'Target type',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'student', label: 'Student' },
        { value: 'teacher', label: 'Teacher' },
        { value: 'manager', label: 'Nalim' },
        { value: 'household', label: 'Family' },
        { value: 'user', label: 'User' },
        { value: 'batch', label: 'Batch' },
        { value: 'fee_cycle', label: 'Fee cycle' },
        { value: 'fee_payment', label: 'Fee payment' },
        { value: 'salary_cycle', label: 'Salary cycle' },
        { value: 'announcement', label: 'Announcement' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'actorRole',
      label: 'Actor role',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'super_admin', label: 'Super admin' },
        { value: 'academic_admin', label: 'Academic admin' },
        { value: 'finance_admin', label: 'Finance admin' },
        { value: 'nalim', label: 'Nalim' },
        { value: 'teacher', label: 'Teacher' },
        { value: 'parent', label: 'Parent' },
        { value: 'student', label: 'Student' },
        { value: 'support_viewer', label: 'Support viewer' },
      ],
    },
    {
      kind: 'date-range',
      key: 'occurred',
      label: 'Occurred',
      group: 'filters',
      params: { from: 'from', to: 'to' },
    },
  ],
  sorts: [
    { value: 'occurredAt:desc', label: 'Newest first' },
    // Registered so the Occurred column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'occurredAt:asc', label: 'Oldest first' },
  ],
  resultNoun: ['event', 'events'],
};

/**
 * Column-header sort binding, synced to the same `sort` param. Only the Occurred
 * column is sortable (it maps to `occurredAt`, the backend's one allow-listed
 * sort field, with both directions registered above).
 */
export const AUDIT_SORT_COLUMNS: Record<'occurredAt', SortColumnBinding> = {
  occurredAt: { field: 'occurredAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Audit log, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/system/audit` opens on the configured defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `occurred` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `from`/`to`; omitting it
 * would stop a preset choice from sticking. `actor` is the sibling exact-id
 * input (not a toolkit facet) and is declared here too so it round-trips through
 * the route boundary — mirroring how the Withdrawals queue declares
 * `academicYear`. Kept beside the config (not in the route) so this boundary is
 * unit-testable without loading the route's React/router side effects.
 */
export const auditSearchSchema = z.object({
  q: z.string().optional(),
  targetType: z.string().optional(),
  actorRole: z.string().optional(),
  occurred: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  actor: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
